var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Transformer, Utils } from '@pacem/pacem-core';
class SuperenalottoUtils {
    static parseCsv(csv) {
        const draws = [];
        const hot = new Map();
        const trendy = new Map();
        const lines = csv.split('\n');
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const columns = line.split('\t');
            const draw = {
                date: columns[0],
                numbers: columns.slice(1, 7).map(n => parseInt(n)),
                jolly: columns[7] && parseInt(columns[7]) || null,
                superstar: columns[8] && parseInt(columns[8]) || null,
            };
            draws.push(draw);
        }
        return draws;
    }
    static hotTuple(csv) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        return SuperenalottoUtils.tupleByFrequency(extractions, 'hot');
    }
    static coldTuple(csv) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        return SuperenalottoUtils.tupleByFrequency(extractions, 'cold');
    }
    static tupleByFrequency(extractions, mode) {
        const posFrequencies = SuperenalottoUtils.positionFrequencies(extractions);
        const lastSeen = Array.from({ length: 6 }, () => new Map());
        for (const ext of extractions) {
            const time = new Date(ext.date).getTime();
            ext.numbers.forEach((num, pos) => {
                if (!(time <= lastSeen[pos].get(num))) {
                    lastSeen[pos].set(num, time);
                }
            });
        }
        const sign = mode === 'hot' ? 1 : -1;
        const better = (a, b) => a.score > b.score || (a.score === b.score && a.recency > b.recency);
        const chains = [];
        for (let pos = 0; pos < 6; pos++) {
            const map = new Map();
            for (const [num, freq] of posFrequencies[pos]) {
                let head = pos === 0 ? { score: 0, recency: 0, prev: -1 } : null;
                let headNum = -1;
                if (pos > 0) {
                    for (const [prevNum, chain] of chains[pos - 1]) {
                        if (prevNum < num && (head === null || better(chain, head))) {
                            head = chain;
                            headNum = prevNum;
                        }
                    }
                }
                if (head !== null) {
                    map.set(num, {
                        score: head.score + sign * freq,
                        recency: head.recency + sign * lastSeen[pos].get(num),
                        prev: headNum
                    });
                }
            }
            chains.push(map);
        }
        let bestNum = -1;
        for (const [num, chain] of chains[5]) {
            if (bestNum < 0 || better(chain, chains[5].get(bestNum))) {
                bestNum = num;
            }
        }
        if (bestNum < 0) {
            return null;
        }
        const numbers = [];
        for (let pos = 5, num = bestNum; pos >= 0; pos--) {
            numbers.unshift(num);
            num = chains[pos].get(num).prev;
        }
        return SuperenalottoUtils.describe(extractions, numbers, posFrequencies);
    }
    static positionFrequencies(extractions) {
        const posFrequencies = Array.from({ length: 6 }, () => new Map());
        for (const ext of extractions) {
            for (let pos = 0; pos < 6; pos++) {
                const num = ext.numbers[pos];
                const freqMap = posFrequencies[pos];
                freqMap.set(num, (freqMap.get(num) || 0) + 1);
            }
        }
        return posFrequencies;
    }
    static describe(extractions, numbers, posFrequencies) {
        const frequencies = numbers.map((num, pos) => posFrequencies[pos].get(num) || 0);
        const set = new Set(numbers);
        let lastMatch = null;
        let lastTime = -Infinity;
        for (const ext of extractions) {
            const matched = ext.numbers.filter(num => set.has(num));
            const time = new Date(ext.date).getTime();
            const matchCount = lastMatch ? lastMatch.matchCount : 0;
            if (matched.length > 0 && (matched.length > matchCount || (matched.length === matchCount && time > lastTime))) {
                lastTime = time;
                lastMatch = {
                    date: ext.date,
                    numbers: matched,
                    matchCount: matched.length
                };
            }
        }
        return { numbers, frequencies, lastMatch };
    }
    static fairness(csv) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        const draws = extractions.length;
        if (!draws) {
            return null;
        }
        const occurrences = new Array(91).fill(0);
        for (const ext of extractions) {
            for (const num of ext.numbers) {
                occurrences[num]++;
            }
        }
        const p = 6 / 90;
        const expected = draws * p;
        const sigma = Math.sqrt(draws * p * (1 - p));
        let chiSquare = 0;
        const counts = [];
        for (let num = 1; num <= 90; num++) {
            const count = occurrences[num];
            chiSquare += (count - expected) * (count - expected) / expected;
            counts.push({ number: num, count, z: (count - expected) / sigma });
        }
        const hottest = counts.reduce((a, b) => b.count > a.count ? b : a);
        const coldest = counts.reduce((a, b) => b.count < a.count ? b : a);
        const degreesOfFreedom = 89;
        const mean = 1 - 2 / (9 * degreesOfFreedom);
        const sd = Math.sqrt(2 / (9 * degreesOfFreedom));
        const z = (Math.cbrt(chiSquare / degreesOfFreedom) - mean) / sd;
        const critical95 = degreesOfFreedom * Math.pow(mean + 1.6449 * sd, 3);
        const pValue = 1 - SuperenalottoUtils.normalCdf(z);
        return {
            draws, expected, sigma, counts,
            hottest: { number: hottest.number, count: hottest.count },
            coldest: { number: coldest.number, count: coldest.count },
            chiSquare, degreesOfFreedom, critical95, pValue
        };
    }
    static normalCdf(z) {
        const t = 1 / (1 + 0.2316419 * Math.abs(z));
        const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
        const tail = Math.exp(-z * z / 2) / Math.sqrt(2 * Math.PI) * poly;
        return z >= 0 ? 1 - tail : tail;
    }
    static unpopularTuple(csv) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        if (!extractions.length) {
            return null;
        }
        const occurrences = new Array(91).fill(0);
        for (const ext of extractions) {
            for (const num of ext.numbers) {
                occurrences[num]++;
            }
        }
        const recent = new Set();
        for (const ext of extractions.slice(-10)) {
            for (const num of ext.numbers) {
                recent.add(num);
            }
        }
        const byOccurrences = occurrences.map((count, num) => ({ num, count })).slice(1).sort((a, b) => a.count - b.count);
        const hotness = new Array(91).fill(0);
        byOccurrences.forEach((item, rank) => hotness[item.num] = rank / 89);
        const popularity = (num) => (num <= 31 ? 1 : 0) + (num <= 12 ? .5 : 0) + hotness[num] + (recent.has(num) ? .5 : 0);
        const candidates = Array.from({ length: 90 }, (_, i) => i + 1)
            .sort((a, b) => popularity(a) - popularity(b) || occurrences[a] - occurrences[b]);
        const numbers = [];
        for (const num of candidates) {
            const clashes = numbers.some(n => Math.abs(n - num) <= 1 || n % 10 === num % 10)
                || numbers.filter(n => Math.floor(n / 10) === Math.floor(num / 10)).length >= 2;
            if (!clashes && numbers.push(num) === 6) {
                break;
            }
        }
        if (numbers.length < 6) {
            return null;
        }
        numbers.sort((a, b) => a - b);
        return SuperenalottoUtils.describe(extractions, numbers, SuperenalottoUtils.positionFrequencies(extractions));
    }
    static optimalTuple(csv, matchThreshold) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        if (!Utils.isNull(matchThreshold)) {
            return SuperenalottoUtils.optimalTupleWithThreshold(extractions, matchThreshold);
        }
        for (let matches = 2; matches < 6; matches++) {
            const result = SuperenalottoUtils.optimalTupleWithExactMatch(extractions, matches);
            if (!Utils.isNull(result)) {
                return result;
            }
        }
        return null;
    }
    static optimalTupleWithThreshold(csv, matchThreshold = 2) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        for (let matches = matchThreshold - 1; matches >= 0; matches--) {
            const result = SuperenalottoUtils.optimalTupleWithExactMatch(extractions, matches);
            if (!Utils.isNull(result)) {
                return result;
            }
        }
        return null;
    }
    static optimalTupleWithExactMatch(csv, matches) {
        const extractions = (typeof csv === 'string' ? SuperenalottoUtils.parseCsv(csv) : csv);
        const drawsByNumber = Array.from({ length: 91 }, () => []);
        extractions.forEach((e, i) => {
            for (const num of e.numbers) {
                drawsByNumber[num].push(i);
            }
        });
        const matchCounts = new Uint8Array(extractions.length);
        const countHist = new Int32Array(8);
        countHist[0] = extractions.length;
        const posFrequencies = SuperenalottoUtils.positionFrequencies(extractions);
        const candidatesPerPos = posFrequencies.map(freqMap => Array.from(freqMap.keys()).sort((a, b) => (freqMap.get(b) || 0) - (freqMap.get(a) || 0)));
        const suffixMaxScore = new Array(7).fill(0);
        for (let pos = 5; pos >= 0; pos--) {
            suffixMaxScore[pos] = suffixMaxScore[pos + 1] + Math.max(0, ...Array.from(posFrequencies[pos].values()));
        }
        let bestTuple = null;
        let maxScore = -1;
        function pick(num) {
            let valid = true;
            for (const i of drawsByNumber[num]) {
                const count = ++matchCounts[i];
                countHist[count - 1]--;
                countHist[count]++;
                if (count > matches) {
                    valid = false;
                }
            }
            return valid;
        }
        function unpick(num) {
            for (const i of drawsByNumber[num]) {
                const count = matchCounts[i]--;
                countHist[count]--;
                countHist[count - 1]++;
            }
        }
        function maxMatchCount() {
            for (let count = 6; count > 0; count--) {
                if (countHist[count] > 0) {
                    return count;
                }
            }
            return 0;
        }
        function search(pos, currentTuple, currentScore) {
            if (pos === 6) {
                if (currentScore > maxScore && countHist[matches] > 0) {
                    maxScore = currentScore;
                    bestTuple = [...currentTuple];
                }
                return;
            }
            if (maxMatchCount() + (6 - pos) < matches) {
                return;
            }
            const minAllowed = pos > 0 ? currentTuple[pos - 1] + 1 : 1;
            for (const num of candidatesPerPos[pos]) {
                const freq = posFrequencies[pos].get(num) || 0;
                if (currentScore + freq + suffixMaxScore[pos + 1] <= maxScore)
                    break;
                if (num < minAllowed)
                    continue;
                if (pick(num)) {
                    currentTuple.push(num);
                    search(pos + 1, currentTuple, currentScore + freq);
                    currentTuple.pop();
                }
                unpick(num);
            }
        }
        search(0, [], 0);
        if (!bestTuple)
            return null;
        return SuperenalottoUtils.describe(extractions, bestTuple, posFrequencies);
    }
}
__decorate([
    Transformer('superenalotto.parse')
], SuperenalottoUtils, "parseCsv", null);
__decorate([
    Transformer('superenalotto.hot')
], SuperenalottoUtils, "hotTuple", null);
__decorate([
    Transformer('superenalotto.cold')
], SuperenalottoUtils, "coldTuple", null);
__decorate([
    Transformer('superenalotto.fairness')
], SuperenalottoUtils, "fairness", null);
__decorate([
    Transformer('superenalotto.unpopular')
], SuperenalottoUtils, "unpopularTuple", null);
__decorate([
    Transformer('superenalotto.optimal')
], SuperenalottoUtils, "optimalTuple", null);
